import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  keyframes,
  Skeleton,
  Container,
  CardActionArea,
  Divider,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import GuitarIcon from '@mui/icons-material/MusicNote';
import AddIcon from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { PlayArrow as PlayArrowIcon, Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { useAudio } from '../context/AudioContext';

// Define the scrolling animation
const scrollText = keyframes`
  0% {
    transform: translateX(0);
  }
  10% {
    transform: translateX(0);
  }
  90% {
    transform: translateX(calc(-100% + var(--container-width)));
  }
  100% {
    transform: translateX(calc(-100% + var(--container-width)));
  }
`;

// Create a styled component for the scrolling text
const ScrollingText = styled(Typography)(({ theme, shouldScroll }) => ({
  display: 'inline-block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  position: 'relative',
  ...(shouldScroll && {
    animation: `${scrollText} 5s linear infinite`,
    '&:hover': {
      animationPlayState: 'paused',
    },
  }),
}));

// Create a PlaylistCard component
const PlaylistCard = ({ playlist, onPlaylistClick, imageErrorStates, handleImageError, handleImageLoad, isSaved }) => {
  // Create refs for the text containers
  const nameContainerRef = useRef(null);
  const styleContainerRef = useRef(null);
  const nameTextRef = useRef(null);
  const styleTextRef = useRef(null);
  const [isNameScrolling, setIsNameScrolling] = useState(false);
  const [isStyleScrolling, setIsStyleScrolling] = useState(false);
  
  // Function to measure and set the container width
  const measureContainerWidth = () => {
    if (nameContainerRef.current && nameTextRef.current) {
      const nameContainerWidth = nameContainerRef.current.offsetWidth;
      const nameTextWidth = nameTextRef.current.offsetWidth;
      
      nameContainerRef.current.style.setProperty('--container-width', `${nameContainerWidth}px`);
      
      // Check if name should scroll based on actual width
      const shouldNameScroll = nameTextWidth > nameContainerWidth;
      setIsNameScrolling(shouldNameScroll);
    }
    
    if (styleContainerRef.current && styleTextRef.current) {
      const styleContainerWidth = styleContainerRef.current.offsetWidth;
      const styleTextWidth = styleTextRef.current.offsetWidth;
      
      styleContainerRef.current.style.setProperty('--container-width', `${styleContainerWidth}px`);
      
      // Check if style should scroll based on actual width
      const shouldStyleScroll = styleTextWidth > styleContainerWidth;
      setIsStyleScrolling(shouldStyleScroll);
    }
  };

  // Measure after render
  useEffect(() => {
    // Use a small timeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      measureContainerWidth();
    }, 50);
    
    return () => clearTimeout(timer);
  }, [playlist.name, playlist.playlistStyle]);
  
  return (
    <Grid item key={playlist._id} sx={{ 
      display: 'flex',
      boxSizing: 'border-box',
      justifyContent: 'center',
      maxWidth: 'calc(50% - 8px)',
      width: '180px',
    }}>
      <Card 
        sx={{ 
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }
        }}
      >
        <CardActionArea 
          onClick={() => onPlaylistClick(playlist._id)}
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch'
          }}
        >
          <CardMedia
            component="div"
            sx={{
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              height: '100px'
            }}
          >
            {playlist.songs && playlist.songs.length > 0 && playlist.songs[0].imageUrl && !imageErrorStates[playlist._id] ? (
              <Box
                component="img"
                height="100px"
                src={playlist.songs[0].imageUrl}
                alt={playlist.name}
                onError={() => handleImageError(playlist._id)}
                onLoad={() => handleImageLoad(playlist._id)}
                sx={{
                  objectFit: 'cover',
                  width: '100%'
                }}
              />
            ) : (
              <MusicNoteIcon sx={{ fontSize: 60, color: 'white' }} />
            )}
          </CardMedia>
          <CardContent sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ 
              overflow: 'hidden',
              width: '100%',
              mb: 1
            }} ref={nameContainerRef}>
              <ScrollingText 
                variant="h6" 
                component="div" 
                shouldScroll={isNameScrolling}
                ref={nameTextRef}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '1rem' // playlist.name.length > 20 ? '0.9rem' : '1rem'
                }}
              >
                {playlist.name}
              </ScrollingText>
            </Box>
            <Box sx={{ 
              overflow: 'hidden',
              width: '100%',
              mb: 1
            }} ref={styleContainerRef}>
              <ScrollingText 
                variant="body2" 
                component="div" 
                shouldScroll={isStyleScrolling}
                ref={styleTextRef}
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.9rem' // playlist.playlistStyle.length > 20 ? '0.8rem' : '0.9rem'
                }}
              >
                {playlist.playlistStyle}
              </ScrollingText>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {playlist.songs ? playlist.songs.length : 0} songs
              </Typography>
              {playlist.instrumental && (
                <Tooltip title="Instrumental (No Lyrics)">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GuitarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { setPlaylistAndCurrentSong } = useAudio();
  const [playlists, setPlaylists] = useState([]);
  const [otherPlaylists, setOtherPlaylists] = useState([]);
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [recentPlaylists, setRecentPlaylists] = useState([]);
  const [savedPlaylistIds, setSavedPlaylistIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [otherPlaylistsLoading, setOtherPlaylistsLoading] = useState(true);
  const [savedPlaylistsLoading, setSavedPlaylistsLoading] = useState(true);
  const [recentPlaylistsLoading, setRecentPlaylistsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', playlistStyle: '' });
  const [error, setError] = useState('');
  const [playlistName, setPlaylistName] = useState('');
  const [playlistStyle, setPlaylistStyle] = useState('');
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [playlistStyleError, setPlaylistStyleError] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [imageErrorStates, setImageErrorStates] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Add a ref to track if we've already logged the state
  const stateLoggedRef = useRef(false);
  
  // Add a ref to store the latest state values
  const stateRef = useRef({
    playlists: [],
    savedPlaylists: [],
    recentPlaylists: [],
    loading: true,
    savedPlaylistsLoading: true,
    recentPlaylistsLoading: true
  });
  
  // Update the ref whenever state changes
  useEffect(() => {
    stateRef.current = {
      playlists,
      savedPlaylists,
      recentPlaylists,
      loading,
      savedPlaylistsLoading,
      recentPlaylistsLoading
    };
  }, [playlists, savedPlaylists, recentPlaylists, loading, savedPlaylistsLoading, recentPlaylistsLoading]);
  
  // Add a separate useEffect to log the initial state once loading is complete
  useEffect(() => {
    if (!loading && !savedPlaylistsLoading && !recentPlaylistsLoading && !stateLoggedRef.current) {
      console.log('Initial state loaded:', {
        playlists,
        savedPlaylists,
        recentPlaylists
      });
      stateLoggedRef.current = true;
    }
  }, [loading, savedPlaylistsLoading, recentPlaylistsLoading, playlists, savedPlaylists, recentPlaylists]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);

    //load subscriptions status on page load from API endpoint
    const fetchSubscriptionStatus = async () => {
      if (!token) {
        localStorage.removeItem('hasSubscription');
        console.log('no token, removing hasSubscription from localStorage');
        return;
      }
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/stripe/subscription-status`, {
        headers: { 'x-auth-token': token }
      });
      console.log('fetchSubscriptionStatus response: ', response.data);
      if(response.data.hasSubscription) {
        console.log('has subscription, setting hasSubscription in localStorage');
        localStorage.setItem('hasSubscription', response.data.hasSubscription);
      } else {
        console.log('no subscription, removing hasSubscription from localStorage');
        localStorage.removeItem('hasSubscription');
      }
    };
    fetchSubscriptionStatus();
    
    // Fetch all playlists using the fetchData function
    fetchData();

    // Add event listener for the custom event
    const handlePlayFirstAvailablePlaylistEvent = () => {
      handlePlayFirstAvailablePlaylist();
    };

    window.addEventListener('playFirstAvailablePlaylist', handlePlayFirstAvailablePlaylistEvent);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('playFirstAvailablePlaylist', handlePlayFirstAvailablePlaylistEvent);
    };
  }, [navigate]);

  const fetchData = async () => {
    try {
      console.log('fetchData: ', playlists, savedPlaylists, recentPlaylists);
      setLoading(true);
      setSavedPlaylistsLoading(true);
      setRecentPlaylistsLoading(true);
      setOtherPlaylistsLoading(true);
      
      await Promise.all([
        fetchPlaylists(),
        fetchOtherPlaylists(),
        fetchSavedPlaylists(),
        fetchRecentPlaylists()
      ]);

      console.log(' done fetching data: ', playlists, savedPlaylists, recentPlaylists);
    } catch (err) {
      console.error('Error fetching playlists:', err);
      setError('Failed to load playlists. Please try again later.');
      // Ensure all loading states are set to false in case of error
      setLoading(false);
      setSavedPlaylistsLoading(false);
      setRecentPlaylistsLoading(false);
      setOtherPlaylistsLoading(false);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      
      console.log('fetching playlists with token: ', token);
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/playlists`, {
        headers: { 'x-auth-token': token }
      });
      console.log('fetchPlaylists response:', response.data);
      setPlaylists(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching playlists:', error);
      if (error.response && error.response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('token');
      } else {
        setError('Failed to fetch playlists. Please try again.');
      }
      setLoading(false);
    }
  };

  // Add useEffect to track all state changes
  useEffect(() => {
    console.log('State updated:', {
      playlists,
      savedPlaylists,
      recentPlaylists,
      loading,
      savedPlaylistsLoading,
      recentPlaylistsLoading
    });
  }, [playlists, savedPlaylists, recentPlaylists, loading, savedPlaylistsLoading, recentPlaylistsLoading]);

  const fetchOtherPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'x-auth-token': token } : {};
      
      console.log('fetching other playlists with token: ', token);
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/playlists/others`, {
        headers
      });
      console.log('fetchOtherPlaylists response:', response.data);
      setOtherPlaylists(response.data);
      setOtherPlaylistsLoading(false);
    } catch (error) {
      console.error('Error fetching other playlists:', error);
      setOtherPlaylistsLoading(false);
    }
  };

  const fetchSavedPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      
      console.log('fetching saved playlists with token: ', token);
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/playlists/saved`, {
        headers: { 'x-auth-token': token }
      });
      console.log('fetchSavedPlaylists response:', response.data);
      setSavedPlaylists(response.data || []);
      const savedIds = new Set(response.data.map(playlist => playlist._id));
      setSavedPlaylistIds(savedIds);
      setSavedPlaylistsLoading(false);
    } catch (error) {
      console.error('Error fetching saved playlists:', error);
      setSavedPlaylistsLoading(false);
    }
  };

  const fetchRecentPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'x-auth-token': token } : {};
      
      console.log('fetching recent playlists with token: ', token);
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/playlists/recent`, {
        headers
      });
      console.log('fetchRecentPlaylists response:', response.data);
      setRecentPlaylists(response.data || []);
      setRecentPlaylistsLoading(false);
    } catch (error) {
      console.error('Error fetching recent playlists:', error);
      setRecentPlaylistsLoading(false);
    }
  };

  const handleCreatePlaylist = async () => {
    // if (!playlistStyle) {
    //   setPlaylistStyleError(true);
    //   return;
    // }
    // setPlaylistStyleError(false);
    // setOpen(false);
    // setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      await axios.post(
        `${baseUrl}/api/playlists`,
        {
          name: playlistName,
          playlistStyle: playlistStyle,
          instrumental: isInstrumental
        },
        {
          headers: { 'x-auth-token': token }
        }
      );
      setOpen(false);
      setNewPlaylist({ name: '', playlistStyle: '' });
      fetchPlaylists();
    } catch (error) {
      console.error('Error creating playlist:', error);
      if (error.response && error.response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to create playlist. Please try again.');
      }
    }
  };

  const handleGenerateSongs = async (playlistId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Find the playlist to get its style
      const playlist = playlists.find(p => p._id === playlistId);
      if (!playlist || !playlist.playlistStyle) {
        setError('Playlist or style not found');
        return;
      }
      
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      await axios.post(
        `${baseUrl}/api/playlists/${playlistId}/generate`,
        { playlistStyle: playlist.playlistStyle },
        {
          headers: { 'x-auth-token': token }
        }
      );
      fetchPlaylists();
    } catch (error) {
      console.error('Error generating songs:', error);
      if (error.response && error.response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError('Failed to generate songs. Please try again.');
      }
    }
  };

  // Function to handle image loading errors
  const handleImageError = (playlistId) => {
    setImageErrorStates(prev => ({
      ...prev,
      [playlistId]: true
    }));
  };
  
  // Function to handle image loading success
  const handleImageLoad = (playlistId) => {
    setImageErrorStates(prev => ({
      ...prev,
      [playlistId]: false
    }));
  };

  // Update the handlePlayFirstAvailablePlaylist function to use the ref
  const handlePlayFirstAvailablePlaylist = () => {
    console.log('handlePlayFirstAvailablePlaylist called');

    // Get the current state values from the ref
    const { playlists: currentPlaylists, savedPlaylists: currentSavedPlaylists, recentPlaylists: currentRecentPlaylists, 
            loading: isLoading, savedPlaylistsLoading: isSavedPlaylistsLoading, recentPlaylistsLoading: isRecentPlaylistsLoading } = stateRef.current;
    
    console.log('Current state in handlePlayFirstAvailablePlaylist:', {
      playlists: currentPlaylists,
      savedPlaylists: currentSavedPlaylists,
      recentPlaylists: currentRecentPlaylists,
      loading: isLoading,
      savedPlaylistsLoading: isSavedPlaylistsLoading,
      recentPlaylistsLoading: isRecentPlaylistsLoading
    });
    
    // Check if playlists are still loading
    if (isLoading && isSavedPlaylistsLoading && isRecentPlaylistsLoading) {
      console.log('Playlists are still loading, waiting...');
      // Wait for playlists to load
      setTimeout(() => {
        handlePlayFirstAvailablePlaylist();
      }, 1000);
      return;
    }
    
    // First check user's playlists
    if (currentPlaylists.length > 0 && currentPlaylists[0].songs && currentPlaylists[0].songs.length > 0) {
      const firstPlaylist = currentPlaylists[0];
      console.log('Playing first user playlist:', firstPlaylist);
      setPlaylistAndCurrentSong(firstPlaylist.songs, firstPlaylist.songs[0], firstPlaylist._id);
      return;
    }
    
    // Then check saved playlists
    if (currentSavedPlaylists.length > 0 && currentSavedPlaylists[0].songs && currentSavedPlaylists[0].songs.length > 0) {
      const firstPlaylist = currentSavedPlaylists[0];
      console.log('Playing first saved playlist:', firstPlaylist);
      setPlaylistAndCurrentSong(firstPlaylist.songs, firstPlaylist.songs[0], firstPlaylist._id);
      return;
    }
    
    // Finally check recent playlists
    if (currentRecentPlaylists.length > 0 && currentRecentPlaylists[0].songs && currentRecentPlaylists[0].songs.length > 0) {
      const firstPlaylist = currentRecentPlaylists[0];
      console.log('Playing first recent playlist:', firstPlaylist);
      setPlaylistAndCurrentSong(firstPlaylist.songs, firstPlaylist.songs[0], firstPlaylist._id);
      return;
    }
    
    // If no playlists with songs are found, show an error or message
    console.log('No playlists with songs available to play');
    setError('No playlists with songs available to play.');
  };

  // Update the existing handlePlaylistClick function
  const handlePlaylistClick = (playlistId) => {
    navigate(`/playlist/${playlistId}`);
  };

  // Replace the renderPlaylistCard function with a simple function that returns the PlaylistCard component
  const renderPlaylistCard = (playlist, isSavedSection = false) => {
    const isSaved = savedPlaylistIds.has(playlist._id);
    const isCreator = playlist.user === localStorage.getItem('userId');
    
    return (
      <PlaylistCard 
        key={playlist._id}
        playlist={playlist}
        onPlaylistClick={handlePlaylistClick}
        imageErrorStates={imageErrorStates}
        handleImageError={handleImageError}
        handleImageLoad={handleImageLoad}
        isSaved={isSaved}
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {!isAuthenticated ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 12,
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderRadius: 4,
            p: 6,
            mb: 6,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="Melo Logo"
            sx={{
              width: '200px',
              height: 'auto',
              mb: 4
            }}
          />
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 3,
              fontSize: {
                xs: '1.4rem', // Extra small devices (phones)
                sm: '2.0rem', // Small devices (tablets)
                md: '2.5rem',  // Medium devices (laptops)
                lg: '3rem'     // Large devices (desktops)
              }
            }}
          >
            Discover Unlimited Royalty-Free Music
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              mb: 4, 
              maxWidth: '600px',
              fontSize: {
                xs: '0.9rem',  // Extra small devices (phones)
                sm: '1rem',    // Small devices (tablets)
                md: '1.1rem',  // Medium devices (laptops)
                lg: '1.25rem'  // Large devices (desktops)
              }
            }}
          >
            Elevate your content with endless musical possibilities. Melo uses advanced AI to craft 100% royalty-free songs perfectly matched to your style and mood. Enjoy custom playlists with fresh, unique music for any project or occasion.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/register')}
              sx={{ 
                borderRadius: '20px',
                textTransform: 'none',
                px: {
                  xs: 2,  // Extra small devices (phones)
                  sm: 2,    // Small devices (tablets)
                  md: 4, // Medium devices (laptops)
                  lg: 4   // Large devices (desktops)
                },
                py: 1.5,
                fontSize: {
                  xs: '0.9rem',  // Extra small devices (phones)
                  sm: '1rem',    // Small devices (tablets)
                  md: '1.05rem', // Medium devices (laptops)
                  lg: '1.1rem'   // Large devices (desktops)
                }
              }}
            >
              Sign Up Now
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ 
                borderRadius: '20px',
                textTransform: 'none',
                px: {
                  xs: 2,  // Extra small devices (phones)
                  sm: 2,    // Small devices (tablets)
                  md: 4, // Medium devices (laptops)
                  lg: 4   // Large devices (desktops)
                },
                py: 1.5,
                fontSize: {
                  xs: '0.9rem',  // Extra small devices (phones)
                  sm: '1rem',    // Small devices (tablets)
                  md: '1.05rem', // Medium devices (laptops)
                  lg: '1.1rem'   // Large devices (desktops)
                }
              }}
            >
              Log In
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Your Playlists
            </Typography>
            {localStorage.getItem('hasSubscription') ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 3
                }}
              >
                Create Playlist
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/subscription')}
                sx={{ 
                  color: 'black',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#3AC9A7',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                Subscribe to generate custom playlists
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : playlists.length === 0 ? (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 8,
                textAlign: 'center'
              }}
            >
              <MusicNoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                You don't have any playlists yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Create your first playlist to start generating music, or listen to existing playlists below
              </Typography>
              {localStorage.getItem('hasSubscription') ? (
                <Button
                  variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 3
                }}
              >
                  Create Playlist
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/subscription')}
                  sx={{ 
                    borderRadius: '20px',
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  Subscribe to generate custom playlists 
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Grid 
                container 
                spacing={'16px'} 
                sx={{ 
                  justifyContent: 'center',
                  margin: '0 auto',
                  maxWidth: '100%',
                  width: '100%'
                }}
              >
                {playlists.map(playlist => renderPlaylistCard(playlist))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Saved Playlists Section - Only show if logged in */}
      {isAuthenticated && savedPlaylists.length > 0 && (
        <>
          <Divider sx={{ my: 6 }} />
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 4 }}>
            Your Saved Playlists
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Grid 
              container 
              spacing={'16px'} 
              sx={{ 
                justifyContent: 'center',
                margin: '0 auto',
                maxWidth: '100%',
                width: '100%'
              }}
            >
              {savedPlaylists.map(playlist => renderPlaylistCard(playlist, true))}
            </Grid>
          </Box>
        </>
      )}

      {/* Recently Generated Playlists Section */}
      <Divider sx={{ my: 6 }} />
      
      <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 4 }}>
        Recently Generated Playlists
      </Typography>

      {recentPlaylistsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : recentPlaylists.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}
        >
          <MusicNoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No recently generated playlists
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Check back later for new playlists
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid 
            container 
            spacing={2} 
            sx={{ 
              justifyContent: 'center',
              margin: '0 auto',
              maxWidth: '100%',
              width: '100%'
            }}
          >
            {recentPlaylists.map(playlist => renderPlaylistCard(playlist))}
          </Grid>
        </Box>
      )}

      {/* Other Popular Playlists Section */}
      <Divider sx={{ my: 6 }} />
      
      <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 4 }}>
        Other Popular Playlists
      </Typography>

      {otherPlaylistsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : otherPlaylists.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}
        >
          <MusicNoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No other playlists available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Check back later for playlists from other users
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid 
            container 
            spacing={2} 
            sx={{ 
              justifyContent: 'center',
              margin: '0 auto',
              maxWidth: '100%',
              width: '100%'
            }}
          >
            {otherPlaylists.map(playlist => renderPlaylistCard(playlist))}
          </Grid>
        </Box>
      )}

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            background: 'linear-gradient(to bottom, #282828, #1a1a1a)',
            color: 'white',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 16px 32px rgba(0,0,0,0.5)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          padding: '20px 24px',
          fontSize: '24px',
          fontWeight: '700',
          letterSpacing: '-0.5px',
          background: 'linear-gradient(to right, #4FB0C6, #3AC9A7)',
          color: 'black'
        }}>
          Create New Playlist
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#B3B3B3' }}>
              Playlist Name (Optional)
            </Typography>
            <TextField
              autoFocus
              fullWidth
              value={playlistName}
              onChange={(e) => setPlaylistName(e.target.value)}
              placeholder="My Awesome Playlist"
              variant="outlined"
              helperText="Leave blank for auto-generated name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#B3B3B3',
                  '&.Mui-focused': {
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: '#B3B3B3',
                  fontSize: '0.75rem',
                  marginLeft: '0',
                  marginTop: '4px',
                },
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#B3B3B3' }}>
              Style <span style={{ color: '#4FB0C6' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              required
              value={playlistStyle}
              onChange={(e) => {
                setPlaylistStyle(e.target.value);
                setPlaylistStyleError(false);
              }}
              placeholder="e.g., Electronic ambient music for studying"
              variant="outlined"
              error={!playlistStyle}
              helperText={!playlistStyle ? "Style is required" : "Be specific about the musical style you want"}
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#B3B3B3',
                  '&.Mui-focused': {
                  },
                  '&.Mui-error': {
                    color: '#f44336',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: playlistStyle ? '#B3B3B3' : '#f44336',
                  fontSize: '0.75rem',
                  marginLeft: '0',
                  marginTop: '4px',
                },
              }}
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isInstrumental}
                onChange={(e) => setIsInstrumental(e.target.checked)}
                color="primary"
              />
            }
            label="Instrumental (no lyrics)"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          padding: '16px 24px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={() => setOpen(false)}
            sx={{ 
              color: '#B3B3B3',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreatePlaylist} 
            variant="contained"
            disabled={!playlistStyle}
            sx={{
              color: 'black',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '20px',
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#3AC9A7',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease'
              },
              '&.Mui-disabled': {
                color: 'rgba(0,0,0,0.5)'
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Home;